import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Spinner from '../ogComponent/spinner';
import Table from '../ogComponent/table';
import { Modal, Box, FormControl, Select, MenuItem } from "@mui/material";
import CustomModal from "../ogComponent/modal";
import { Toast } from 'primereact/toast';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IconEdit, IconTrash } from '@tabler/icons-react';

const PositionDetails = () => {
  const toast = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("All Branches");
  const [branches, setBranches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Access permissions array from Redux store
  const permissions = useSelector((state) => state.emp.permissions);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    outline: "none",
    borderRadius: '10px',
  };

  const header = [
    { name: "Si No", label: "#", active: true },
    { name: "name", label: "Name", active: true },
    { name: "branch", label: "Branch", active: true },
    { name: "contact_info", label: "Contact Info", active: true },
    { name: "Status", label: "Active", active: true },
  ];

  const pagePermissions = ["editjobpositionID", "deletejobpositionID", "employeedetailsID"];

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDeleteClick = () => {
    if (position?.employee_count > 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Cannot Delete Position',
        detail: "This position contains employees. Please remove employees before deleting.",
        life: 3000,
      });
    } else {
      setOpenDeleteModal(true);
    }
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      await axios.delete(
        `${process.env.REACT_APP_API}/api/employee/jobpositions/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOpenDeleteModal(false);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: "Position Deleted Successfully",
        life: 3000,
      });

      setTimeout(() => {
        navigate('/user/positions/');
      }, 3000);
    } catch (error) {
      console.error('Error deleting position:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: "Failed to delete position",
        life: 3000,
      });
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleModalClose = () => {
    setShowEditModal(false);
  };

  const handleRowClick = permissions.includes("employeedetailsID")
    ? (item) => navigate(`/user/empdetails/${item.id}`)
    : undefined;

  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
  };

  const formatText = (text) => {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
  };

  const handleEditSubmit = async (data) => {
    const token = localStorage.getItem('accessToken');
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API}/api/employee/jobpositions/${id}/`,
        { ...data, department: data.department || position?.department?.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosition(response.data);
      setShowEditModal(false);
      toast.current.show({ severity: 'success', summary: 'Success', detail: "Position Updated Successfully", life: 3000 });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: "Failed to update position", life: 3000 });
    }
  };

  // Consolidated fetch for position, departments, and branches
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('accessToken');
      try {
        // Fetch position details
        const positionResponse = await axios.get(
          `${process.env.REACT_APP_API}/api/employee/jobpositions/${id}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPosition(positionResponse.data);
        setCurrentDepartment(positionResponse.data?.department_details);

        // Fetch department options
        const departmentResponse = await axios.get(
          `${process.env.REACT_APP_API}/api/employee/departments/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const formattedDepartments = departmentResponse.data.map(department => ({
          label: department.dep_name,
          value: department.id
        }));
        setDepartmentOptions(formattedDepartments);

        // Fetch branches
        const branchResponse = await axios.get(
          `${process.env.REACT_APP_API}/api/employee/branches/?jobposition=${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBranches(branchResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem("accessToken");
      setLoading(true);
      try {
        let url = `${process.env.REACT_APP_API}/api/account/users-list/?job_position=${id}`;
        if (selectedBranch !== "All Branches") {
          url += `&branch=${selectedBranch}`;
        }
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFilteredEmployees(response.data || []);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [id, selectedBranch]);

  const editModalFields = [
    {
      name: "job_title",
      label: "Job Title",
      type: "text",
      value: formatText(position?.job_title) || "",
      required: true,
    },
    {
      name: "department",
      label: "Department",
      type: "select",
      options: departmentOptions,
      value: currentDepartment ? { label: currentDepartment.name, value: currentDepartment.id } : null,
      required: true,
    },
  ];

  const transformEmployeeData = (employees) => {
    if (!employees || !Array.isArray(employees)) return [];
    return employees.map((employee, index) => ({
      id: employee.id,
      "Si No": index + 1,
      name: (
        <td className="d-flex gap-3 align-items-center px-0" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <img
            style={{ width: "25px", height: "25px", flexShrink: 0, borderRadius: '50%' }}
            src={employee.profile_photo ? employee.profile_photo : "/assets/user.png"}
            alt="user img"
          />
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{employee.name || "N/A"}</div>
        </td>
      ),
      branch: employee.branch?.branch_name || "N/A",
      contact_info: employee.phone_number || "N/A",
      Status: (
        <div style={{ color: employee.status ? 'green' : 'tomato' }}>
          <span
            style={{
              display: 'inline-block',
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: employee.status ? 'green' : 'tomato',
              marginRight: '5px',
            }}
          ></span>
          {employee.status ? 'Active' : 'Inactive'}
        </div>
      ),
    }));
  };

  if (loading) {
    return <Spinner />;
  }

  if (!position) {
    return <p>Position not found.</p>;
  }

  return (
    <>
      <style>
        {`
          @media (max-width: 500px) {
            .modalmainbox {
              min-width: 75% !important;
            }
          }
        `}
      </style>
      <div className="singlePositionContainer px-4 pt-4 w-100">
        <Toast ref={toast} position="bottom-right" />
        <h4>{position.job_title}</h4>
        <table>
          <tr>
            <td className='ps-0'>Department:</td>
            <td>{position.department_details?.name || "N/A"}</td>
          </tr>
          <tr>
            <td className='ps-0'>Employees:</td>
            <td>{position.employee_count || "0"}</td>
          </tr>
        </table>

        <div className="employeeTableContainer mt-4 w-100">
          <div className="d-flex justify-content-end mb-3 gap-2 gap-md-3">
            <FormControl variant="outlined" style={{ minWidth: 200 }}>
              <Select
                value={selectedBranch}
                onChange={handleBranchChange}
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
              >
                <MenuItem value="All Branches">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {permissions.includes("editjobpositionID") && (
              <button
                className="btn btn-outline-primary"
                onClick={handleEditClick}
                data-bs-toggle="modal"
                data-bs-target="#staticBackdrop"
              >
                <IconEdit stroke={2} />
              </button>
            )}
            {permissions.includes("deletejobpositionID") && (
              <button className="btn btn-outline-danger" onClick={handleDeleteClick}>
                <IconTrash stroke={2} />
              </button>
            )}
          </div>
          {filteredEmployees.length > 0 ? (
            <Table
              header={header}
              product={transformEmployeeData(filteredEmployees)}
              onRowClick={handleRowClick}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-end position-relative" style={{ height: "50vh" }}>
              <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                backgroundImage: 'url(/assets/file.png)',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.3,
              }}></div>
              <p className="m-0 fw-light fs-5 position-relative">No Employees Found!</p>
            </div>
          )}
        </div>
        {permissions.includes("editjobpositionID") && (
          <CustomModal
            heading="Edit Position Data"
            fields={editModalFields}
            onSubmit={handleEditSubmit}
            type={"department"}
          />
        )}
        <Modal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ outline: "none" }}
        >
          <Box
            sx={{ ...modalStyle, outline: "none", padding: "25px" }}
            className="modalmainbox"
          >
            <div className="w-100 d-flex align-items-center flex-column">
              <WarningAmberIcon
                sx={{ color: "#FFA000", fontSize: 60, width: "100%" }}
              />
              <Box
                sx={{
                  fontWeight: "bold",
                  fontSize: "clamp(13px, 1.4vw, 17px)",
                  mb: 2,
                }}
              >
                Confirm Delete
              </Box>
              <Box
                sx={{
                  color: "#555",
                  mb: 2,
                  fontSize: "clamp(12px, 1.2vw, 14px)",
                  textWrap: "wrap",
                  textAlign: "center",
                  paddingX: "10px",
                }}
              >
                Are you sure you want to delete this position?
              </Box>
            </div>
            <div className="w-100 d-flex justify-content-between flex-row-reverse gap-3">
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                style={{ fontSize: "clamp(12px, 1.2vw, 15px)" }}
              >
                Confirm
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleCloseDeleteModal}
                style={{ fontSize: "clamp(12px, 1.2vw, 15px)" }}
              >
                Cancel
              </button>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
};

export default PositionDetails;